import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { type DateTime } from 'luxon';

import { ApiClient } from './api.client.js';
import { type CodeCategoryViewMode } from '../models/index.js';

export interface CheckInReportDTO {
  patientId: string;
  patientName: string;
  timezone: string;
  report: CheckInReportDetails;
}

export interface CheckInReportDetails {
  header: string[];
  data: CheckinDailyReport[];
}

export interface CheckinDailyReport {
  time: string;
  id: string;
  name: string;
  color: string;
  isScheduled: boolean;
}

export interface DailyReport {
  patientId: string;
  patientName: string;
  timezone: string;
  report: ReportDetails[];
}

export interface ReportDetails {
  durationReport: DurationReport[];
  headerReport: HeaderReport[];
  startDate: string;
  endDate: string;
  logs: LogDetails[];
  eventId: string;
}

export interface DurationReport {
  categoryId: string;
  name: string;
  sum: string;
  percentage: string;
  color: string;
}

export interface HeaderReport {
  categoryId: string;
  name: string;
  sum: number;
  color: string;
  type: CodeCategoryViewMode;
}

export interface LogDetails {
  time: string;
  id: string;
  name: string;
  color: string;
  duration?: number;
}

/**
 * Which logs a report counts. `user` excludes anything a monitor recorded by
 * reviewing an AI-classified activity event; `all` keeps both but a
 * user-entered log still wins its check-in slot.
 */
export type LogSource = 'user' | 'ai' | 'all';

export const ReportsApiKeys = {
  DailyReport: 'DailyReport',
  CheckInReport: 'CheckInReport',
};

export class ReportsApi extends ApiClient {
  public static async getDailyReportFor(
    patientId: string,
    startDate: DateTime,
    endDate: DateTime,
    logSource: LogSource,
  ): Promise<DailyReport> {
    if (!patientId) {
      return Promise.reject(new Error('[getDailyReportFor] Invalid PatientId'));
    }
    return this.http
      .get(`/reports/daily`, {
        params: {
          patientId: patientId,
          startDate: startDate.toISO(),
          endDate: endDate.toISO(),
          logSource,
        },
      })
      .then((res) => res.data);
  }

  public static async getCheckInReportFor(
    patientId: string,
    startDate: DateTime,
    endDate: DateTime,
    logSource: LogSource,
  ): Promise<CheckInReportDTO> {
    if (!patientId) {
      return Promise.reject(new Error('[getDailyReportFor] Invalid PatientId'));
    }
    return this.http
      .get(`/reports/check-in`, {
        params: {
          patientId: patientId,
          startDate: startDate.toISO(),
          endDate: endDate.toISO(),
          logSource,
        },
      })
      .then((res) => res.data);
  }
}

export function useDailyReportForPatient(
  patientId: string,
  startDate: DateTime,
  endDate: DateTime,
  logSource: LogSource,
  queryOptions?: UseQueryOptions<DailyReport>,
) {
  return useQuery<DailyReport>(
    [ReportsApiKeys.DailyReport, patientId, startDate.toISODate(), endDate.toISODate(), logSource],
    () => ReportsApi.getDailyReportFor(patientId, startDate, endDate, logSource),
    queryOptions,
  );
}

export function useCheckInReportForPatient(
  patientId: string,
  startDate: DateTime,
  endDate: DateTime,
  logSource: LogSource,
  queryOptions?: UseQueryOptions<CheckInReportDTO>,
) {
  return useQuery<CheckInReportDTO>(
    [ReportsApiKeys.CheckInReport, patientId, startDate.toISODate(), endDate.toISODate(), logSource],
    () => ReportsApi.getCheckInReportFor(patientId, startDate, endDate, logSource),
    queryOptions,
  );
}
